<script>
export default { name: 'UiInput' };
</script>

<script setup>
import { computed, ref } from 'vue';
import { Eye, EyeOff } from 'lucide-vue-next';

let uidSeed = 0;

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  // text | password | email | date | number | tel | url
  type: { type: String, default: 'text' },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  // Componente lucide a mostrar a la izquierda, ej. :icon="User"
  icon: { type: [Object, Function, String], default: null },
  maxlength: { type: [String, Number], default: null },
  // Atributos numéricos (type="number"): min/max/step pasan directo al input.
  min: { type: [String, Number], default: null },
  max: { type: [String, Number], default: null },
  step: { type: [String, Number], default: null },
  autocomplete: { type: String, default: 'off' },
  id: { type: String, default: null },
});

const emit = defineEmits(['update:modelValue', 'change', 'focus', 'blur']);

const showPassword = ref(false);
const fieldId = props.id || `ui-input-${++uidSeed}`;

const effectiveType = computed(() =>
  props.type === 'password' ? (showPassword.value ? 'text' : 'password') : props.type
);

const inputClass = computed(() => [
  'w-full rounded-xl border bg-white dark:bg-slate-950 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 shadow-sm transition-all outline-none placeholder-slate-400 dark:placeholder-slate-600 focus:ring-4',
  props.error
    ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
    : 'border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:ring-brand-500/10',
  props.icon ? 'pl-10' : '',
  props.type === 'password' ? 'pr-10 no-uppercase' : '',
  props.readonly ? 'bg-slate-50 dark:bg-slate-800/60 cursor-not-allowed' : '',
]);
</script>

<template>
  <div>
    <label v-if="label" :for="fieldId" class="block text-xs font-semibold text-slate-500 uppercase mb-1">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <component
        v-if="icon"
        :is="icon"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
        aria-hidden="true"
      />

      <input
        :id="fieldId"
        :type="effectiveType"
        :value="props.modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :step="step"
        :autocomplete="autocomplete"
        :class="inputClass"
        @input="emit('update:modelValue', $event.target.value)"
        @change="$emit('change', $event)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />

      <button
        v-if="type === 'password'"
        type="button"
        :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
        class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        @click="showPassword = !showPassword"
      >
        <EyeOff v-if="showPassword" class="w-4 h-4" />
        <Eye v-else class="w-4 h-4" />
      </button>
    </div>

    <p v-if="error" class="text-xs text-red-500 mt-1">{{ error }}</p>
    <p v-else-if="hint" class="text-xs text-slate-400 mt-1">{{ hint }}</p>
  </div>
</template>